import { apiClient } from '@/lib/api'
import { API_CONFIG } from '@/config/api'
import { getAuthToken } from '@/stores/authStore'
import type {
  SupportChatEvent,
  SupportMessage,
  SupportThread,
  SupportThreadListResult,
  SupportUnreadSummary,
} from '@/services/supportChatService'

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue
    sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

/** Prefer HttpOnly cookies on same-origin; only put JWT in the URL when cross-origin. */
function appendAccessTokenIfNeeded(url: string, hasQuery: boolean): string {
  try {
    const apiOrigin = new URL(API_CONFIG.BASE_URL, window.location.origin).origin
    if (apiOrigin === window.location.origin) return url
  } catch {
    /* fall through and append */
  }
  const token = getAuthToken() || localStorage.getItem('auth-token')
  if (!token) return url
  return `${url}${hasQuery ? '&' : '?'}access_token=${encodeURIComponent(token)}`
}

const quiet = { suppressErrorToast: true } as const

/** Tenant staff → platform shop support APIs. */
export const platformSupportService = {
  async getContext() {
    return apiClient.get<{
      success: boolean
      data: { platform_shop_id: number; customer_id: number }
    }>('/support/platform/context', quiet)
  },

  async listThreads(params?: {
    status?: string
    page?: number
    limit?: number
    kind?: 'ticket' | 'chat'
  }) {
    return apiClient.get<{ success: boolean; data: SupportThreadListResult }>(
      `/support/platform/threads${qs(params || {})}`,
      quiet,
    )
  },

  async createThread(body: {
    subject?: string
    body: string
    ticket_type?: string
    files?: File[]
  }) {
    if (body.files?.length) {
      const form = new FormData()
      form.append('body', body.body || '')
      if (body.subject) form.append('subject', body.subject)
      if (body.ticket_type) form.append('ticket_type', body.ticket_type)
      for (const file of body.files) {
        form.append('files', file)
      }
      return apiClient.post<{
        success: boolean
        data: { thread: SupportThread; message: SupportMessage }
      }>('/support/platform/threads', form)
    }
    return apiClient.post<{
      success: boolean
      data: { thread: SupportThread; message: SupportMessage }
    }>('/support/platform/threads', {
      subject: body.subject,
      body: body.body,
      ticket_type: body.ticket_type,
    })
  },

  async listMessages(threadId: number, params?: { after_message_id?: number; limit?: number }) {
    return apiClient.get<{ success: boolean; data: SupportMessage[] }>(
      `/support/platform/threads/${threadId}/messages${qs(params || {})}`,
      quiet,
    )
  },

  async postMessage(threadId: number, body: { body: string; files?: File[] }) {
    if (body.files?.length) {
      const form = new FormData()
      form.append('body', body.body || '')
      for (const file of body.files) {
        form.append('files', file)
      }
      return apiClient.post<{ success: boolean; data: SupportMessage }>(
        `/support/platform/threads/${threadId}/messages`,
        form,
      )
    }
    return apiClient.post<{ success: boolean; data: SupportMessage }>(
      `/support/platform/threads/${threadId}/messages`,
      body,
    )
  },

  async markRead(threadId: number, lastReadMessageId?: number) {
    return apiClient.post<{ success: boolean }>(
      `/support/platform/threads/${threadId}/read`,
      { last_read_message_id: lastReadMessageId },
      quiet,
    )
  },

  async getUnread() {
    return apiClient.get<{ success: boolean; data: SupportUnreadSummary }>(
      '/support/platform/unread',
      quiet,
    )
  },

  openEventStream(opts: {
    threadId?: number
    onEvent: (event: SupportChatEvent) => void
    onError?: () => void
  }): () => void {
    const params = qs({ thread_id: opts.threadId })
    const base = API_CONFIG.BASE_URL.replace(/\/$/, '')
    const url = appendAccessTokenIfNeeded(`${base}/support/platform/events${params}`, Boolean(params))

    const es = new EventSource(url, { withCredentials: true })
    const types = ['connected', 'thread.created', 'thread.updated', 'message.created'] as const
    for (const type of types) {
      es.addEventListener(type, (e) => {
        try {
          const data = JSON.parse((e as MessageEvent).data)
          opts.onEvent(type === 'connected' ? { type: 'connected' } : data)
        } catch {
          /* ignore */
        }
      })
    }
    es.onerror = () => opts.onError?.()
    return () => es.close()
  },
}
